/**
 * ResidentDocuments — 12-slot document manager for a resident.
 * Modes: view (preview), download, replace (admin/warden/hostel_manager),
 * delete (admin only). Enforces PDF/JPG/PNG + MAX_UPLOAD_MB (server-side).
 */
import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Upload, FileText, ImageIcon, Eye, Download, Trash2, RefreshCw, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DOC_SLOTS = [
  { v: "aadhaar_front", l: "Aadhaar Card (Front)", required: true },
  { v: "aadhaar_back", l: "Aadhaar Card (Back)", required: true },
  { v: "pan", l: "PAN Card", required: true },
  { v: "passport", l: "Passport", required: false },
  { v: "driving_license", l: "Driving License", required: false },
  { v: "id_card", l: "Student/Employee ID", required: true },
  { v: "photograph", l: "Passport-Size Photograph", required: true },
  { v: "admission_form", l: "Admission Form", required: true },
  { v: "agreement", l: "Agreement / Undertaking", required: true },
  { v: "police_verification", l: "Police Verification", required: false },
  { v: "medical_certificate", l: "Medical Certificate", required: false },
  { v: "other", l: "Other Supporting Documents", required: false },
];

const ALLOWED_MIMES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("File read failed"));
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

export function ResidentDocumentsButton({ resident, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={()=>setOpen(true)}
        data-testid={`docs-open-${resident.id}`}>
        <FileText className="w-3.5 h-3.5 mr-1.5" /> Documents
      </Button>
      {open && (
        <ResidentDocumentsDialog resident={resident} open={open}
          onOpenChange={setOpen} onChanged={onChanged} />
      )}
    </>
  );
}

function ResidentDocumentsDialog({ resident, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const canWrite = ["admin", "warden", "hostel_manager"].includes(user?.role);
  const canDelete = user?.role === "admin";

  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null);        // slot key currently uploading
  const [preview, setPreview] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const inputsRef = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get(`/residents/${resident.id}/documents`);
      setDocs(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resident.id]);

  const bySlot = docs.reduce((m, d) => ({ ...m, [d.doc_type]: d }), {});
  const totalSize = docs.reduce((n, d) => n + (d.size || 0), 0);

  const upload = async (slot, file, existing) => {
    if (!file) return;
    if (!ALLOWED_MIMES.includes(file.type)) { toast.error("Only PDF, JPG, PNG accepted"); return; }
    setBusy(slot);
    try {
      const dataUrl = await fileToDataUrl(file);
      const payload = { doc_type: slot, filename: file.name,
                        mime_type: file.type, data_url: dataUrl };
      if (existing) {
        await api.put(`/documents/${existing.id}`, payload);
        toast.success("Replaced");
      } else {
        await api.post(`/residents/${resident.id}/documents`, payload);
        toast.success("Uploaded");
      }
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); if (inputsRef.current[slot]) inputsRef.current[slot].value = ""; }
  };

  const doDelete = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted"); await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirmDel(null); }
  };

  const doPreview = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doDownload = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      const a = document.createElement("a");
      a.href = data.data_url; a.download = data.filename || `${data.doc_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Documents — {resident.name}</DialogTitle>
            <DialogDescription>
              12 slots · PDF / JPG / PNG · max 5 MB each · {docs.length}/{DOC_SLOTS.length} filed
              {totalSize > 0 && <> · ~{(totalSize / (1024 * 1024)).toFixed(1)} MB total</>}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {DOC_SLOTS.map((s) => {
              const doc = bySlot[s.v];
              const isImg = doc?.mime_type?.startsWith("image/");
              return (
                <Card key={s.v} className="p-3 border-border bg-white"
                  data-testid={`doc-slot-${s.v}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.l}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                        {s.required ? <Badge variant="outline" className="text-[9px] h-4 px-1">REQUIRED</Badge>
                                    : <Badge variant="secondary" className="text-[9px] h-4 px-1">OPTIONAL</Badge>}
                        {doc && <>
                          <span className="truncate">{doc.filename}</span>
                          <span>·</span>
                          <span>{(doc.size / 1024).toFixed(0)} KB</span>
                        </>}
                      </div>
                    </div>
                    {isImg ? <ImageIcon className="w-4 h-4 text-primary shrink-0" />
                           : doc ? <FileText className="w-4 h-4 text-primary shrink-0" />
                                 : <div className="w-4" />}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {doc && (
                      <>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={()=>doPreview(doc)}
                          data-testid={`doc-preview-${s.v}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={()=>doDownload(doc)}
                          data-testid={`doc-download-${s.v}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Download
                        </Button>
                      </>
                    )}
                    {canWrite && (
                      <label className="inline-flex">
                        <Button variant="outline" size="sm" className="h-7 px-2 cursor-pointer"
                          asChild disabled={busy === s.v}>
                          <span data-testid={`doc-upload-${s.v}`}>
                            {doc ? <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Replace</>
                                 : <><Upload   className="w-3.5 h-3.5 mr-1" /> Upload</>}
                          </span>
                        </Button>
                        <input
                          ref={(el) => (inputsRef.current[s.v] = el)}
                          type="file" className="hidden" accept="application/pdf,image/*"
                          onChange={(e) => upload(s.v, e.target.files?.[0], doc)}
                        />
                      </label>
                    )}
                    {doc && canDelete && (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive"
                        onClick={() => setConfirmDel(doc)}
                        data-testid={`doc-delete-${s.v}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview overlay */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{preview.filename}</DialogTitle>
                <DialogDescription>
                  Uploaded by {preview.uploaded_by_name} on {new Date(preview.uploaded_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              {preview.mime_type?.startsWith("image/") ? (
                <img src={preview.data_url} alt={preview.filename}
                  className="max-h-[70vh] w-auto mx-auto rounded-md border border-border" />
              ) : (
                <iframe src={preview.data_url} title="doc"
                  className="w-full h-[70vh] border border-border rounded-md" />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirmDel?.filename}"?</AlertDialogTitle>
            <AlertDialogDescription>This action is irreversible and logged in the audit trail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => doDelete(confirmDel)}
              data-testid="doc-delete-confirm"
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
